import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { listElements } from '../../../api/elements.js'
import { listGroupConfigs } from '../../../api/groupConfigs.js'
import {
  deleteScheduler,
  getScheduler,
  setSchedulerStatus,
  updateScheduler,
} from '../../../api/schedulers.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'
import { validateSchedulerForm } from './schedulerValidation.js'

const DATE_FORMAT_OPTIONS = ['timestamp', 'iso8601']

function formatDate(value) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

export default function SchedulerDetailPage() {
  const { applicationId } = useOutletContext()
  const { confirm } = useFeedback()
  const { schedulerId } = useParams()
  const navigate = useNavigate()

  const [state, setState] = useState({
    status: 'loading',
    scheduler: null,
    groupConfigs: [],
    elements: [],
    error: null,
  })
  const [values, setValues] = useState({
    groupConfigId: '',
    elementId: '',
    name: '',
    startDate: '',
    endDate: '',
    outputFormatTime: 'timestamp',
    duration: '',
    isDefault: false,
    isFrozen: false,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isChangingStatus, setIsChangingStatus] = useState(false)

  useEffect(() => {
    let cancelled = false

    Promise.all([
      getScheduler(applicationId, schedulerId),
      listGroupConfigs(applicationId),
      listElements(applicationId),
    ])
      .then(([scheduler, groupConfigs, elements]) => {
        if (cancelled) return
        setState({
          status: 'ok',
          scheduler,
          groupConfigs,
          elements,
          error: null,
        })
        setValues({
          groupConfigId: scheduler.groupConfigId == null ? '' : String(scheduler.groupConfigId),
          elementId: scheduler.elementId == null ? '' : String(scheduler.elementId),
          name: scheduler.name || '',
          startDate: scheduler.startDate || '',
          endDate: scheduler.endDate || '',
          outputFormatTime: scheduler.outputFormatTime || 'timestamp',
          duration: scheduler.duration == null ? '' : String(scheduler.duration),
          isDefault: Boolean(scheduler.isDefault),
          isFrozen: Boolean(scheduler.isFrozen),
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState({
          status: 'error',
          scheduler: null,
          groupConfigs: [],
          elements: [],
          error,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, schedulerId])

  const filteredElements = useMemo(() => {
    if (!values.groupConfigId) return state.elements
    return state.elements.filter((element) => String(element.groupConfigId || '') === String(values.groupConfigId))
  }, [state.elements, values.groupConfigId])

  function onChange(field) {
    return (event) => {
      const isCheckbox = field === 'isDefault' || field === 'isFrozen'
      const nextValue = isCheckbox ? event.target.checked : event.target.value
      setValues((prev) => {
        const next = { ...prev, [field]: nextValue }
        if (field === 'groupConfigId') {
          next.elementId = ''
        }
        return next
      })
    }
  }

  async function handleSave() {
    if (isSaving || !state.scheduler) return

    const validation = validateSchedulerForm(values)
    if (!validation.success) {
      setState((prev) => ({
        ...prev,
        error: new Error(validation.error),
      }))
      return
    }

    try {
      setIsSaving(true)
      const updated = await updateScheduler(applicationId, state.scheduler.id, validation.data)
      setState((prev) => ({
        ...prev,
        scheduler: updated,
        error: null,
      }))
    } catch (error) {
      const detail = error?.body?.detail
      setState((prev) => ({
        ...prev,
        error: new Error(detail ? String(detail) : String(error?.message || error)),
      }))
    } finally {
      setIsSaving(false)
    }
  }

  async function handleSetStatus(newStatus) {
    if (isChangingStatus || !state.scheduler) return
    try {
      setIsChangingStatus(true)
      setState((prev) => ({ ...prev, error: null }))
      const updated = await setSchedulerStatus(applicationId, state.scheduler.id, newStatus)
      setState((prev) => ({
        ...prev,
        scheduler: updated,
        error: null,
      }))
    } catch (error) {
      const detail = error?.body?.detail
      setState((prev) => ({
        ...prev,
        error: new Error(detail ? String(detail) : String(error?.message || error)),
      }))
    } finally {
      setIsChangingStatus(false)
    }
  }

  async function handleDelete() {
    if (isDeleting || !state.scheduler) return
    const confirmed = await confirm({
      title: 'Delete scheduler',
      message: 'Delete this scheduler? This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      setIsDeleting(true)
      await deleteScheduler(applicationId, state.scheduler.id)
      navigate(`/applications/${applicationId}/scheduler`)
    } catch (error) {
      const detail = error?.body?.detail
      setState((prev) => ({
        ...prev,
        error: new Error(detail ? String(detail) : String(error?.message || error)),
      }))
    } finally {
      setIsDeleting(false)
    }
  }

  if (state.status === 'loading') {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Loading scheduler...</div>
      </section>
    )
  }

  if (state.status === 'error' || !state.scheduler) {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Scheduler not found</div>
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error || '')}</pre>
        <div className="sectionActions">
          <Link to={`/applications/${applicationId}/scheduler`} className="button buttonGhost">
            Back to scheduler
          </Link>
        </div>
      </section>
    )
  }

  const scheduler = state.scheduler

  return (
    <div className="page">
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">Scheduler: {scheduler.name || 'Untitled'}</h1>
          <div className="muted">
            Created: {formatDate(scheduler.createdAt)}
          </div>
        </div>
        <div className="buttonGroup">
          <Link to={`/applications/${applicationId}/scheduler`} className="button buttonGhost">
            Back
          </Link>
          <label className="formField formFieldInline formFieldInlineTight">
            <span className="formLabel formLabelNoMargin">Status</span>
            <select
              className="textInput selectStatus"
              value={scheduler.status || 'Draft'}
              onChange={(e) => {
                const next = e.target.value
                if (next !== (scheduler.status || 'Draft')) handleSetStatus(next)
              }}
              disabled={isChangingStatus}
              aria-label="Scheduler status"
            >
              {(() => {
                const current = scheduler.status || 'Draft'
                const options = current === 'Draft' ? ['Draft', 'Published'] : current === 'Published' ? ['Published', 'Archived'] : ['Archived']
                return options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))
              })()}
            </select>
          </label>
          <button type="button" className="button buttonGhost buttonDanger" onClick={handleDelete} disabled>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
          <button type="button" className="button buttonPrimary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {state.error ? <div className="formError">{String(state.error?.message || state.error)}</div> : null}

      <section className="card formCard">
        <div className="cardTitle">Edit</div>
        <div className="formGrid">
          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} onChange={onChange('name')} />
          </label>

          <label className="formField">
            <span className="formLabel">Group config</span>
            <select className="textInput" value={values.groupConfigId} onChange={onChange('groupConfigId')}>
              <option value="">Choose group config</option>
              {state.groupConfigs.map((groupConfig) => (
                <option key={groupConfig.id} value={groupConfig.id}>
                  {groupConfig.name || `Group #${groupConfig.id}`}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Element</span>
            <select className="textInput" value={values.elementId} onChange={onChange('elementId')}>
              <option value="">Choose element</option>
              {filteredElements.map((element) => (
                <option key={element.id} value={element.id}>
                  {element.name || `Element #${element.id}`} {element.version ? `v${element.version}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Start date (cron)</span>
            <input className="textInput mono" value={values.startDate} onChange={onChange('startDate')} />
          </label>

          <label className="formField">
            <span className="formLabel">End date (cron)</span>
            <input className="textInput mono" value={values.endDate} onChange={onChange('endDate')} />
          </label>

          <label className="formField">
            <span className="formLabel">Duration</span>
            <input
              className="textInput"
              type="number"
              min="0"
              value={values.duration}
              onChange={onChange('duration')}
            />
          </label>

          <label className="formField">
            <span className="formLabel">Output format</span>
            <select className="textInput" value={values.outputFormatTime} onChange={onChange('outputFormatTime')}>
              {DATE_FORMAT_OPTIONS.map((format) => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
          </label>

          <label className="formField formFieldInline">
            <input type="checkbox" checked={values.isDefault} onChange={onChange('isDefault')} />
            <span className="formLabel formLabelNoMargin">
              Is default
            </span>
          </label>

          <label className="formField formFieldInline">
            <input type="checkbox" checked={values.isFrozen} onChange={onChange('isFrozen')} />
            <span className="formLabel formLabelNoMargin">
              Is frozen
            </span>
          </label>
        </div>
      </section>
    </div>
  )
}
